import React, { useContext, useState, useEffect } from 'react';
import { cloneDeep, times } from 'lodash';

import { useTranslation } from "react-i18next";

import { RestApiContext } from '../../contexts/RestApiContext';
import { PluginContext } from '../../contexts/PluginContext';

import { useAggregatedState, usePageReadyState, useBusyState, isDev } from '../../../helpers';
import { MenuItemValidateSchema, SettingsValidateSchema, MenuItemTypes } from '../../../helpers/Common';

export const PageContext = React.createContext(null);

const { Provider } = PageContext;

export const PageProvider = ({ ...props }) => {
    const [pageIsReady, pageHasError, setPageIsReady] = usePageReadyState({});
    const [busy, isBusy, setIsBusy] = useBusyState({});
    const [state, setState] = useAggregatedState({
        license: null,
        settings: null,
        menuItems: null,
        isLegacy: false,
        categories: null,
        pages: null,
        icons: null,
        qrLiveViewer: null,
        configStatus: null,
        configHasChanges: false,
        errors: [],
    });

    const { t, i18n } = useTranslation();

    const {
        plugin
    } = useContext(PluginContext);

    const apiClient = useContext(RestApiContext);

    const checkLicenseAsync = async () => {
        try {
            const license = await apiClient.checkLicenseAsync();
            setState({ license });
        } catch (e) {
            console.debug(e);
        }
    }

    const getSettingsAsync = async () => {
        try {
            const settings = await apiClient.getSettingsAsync();
            const areLegacySettings = settings["AppID"] !== undefined;
            setState({ settings: areLegacySettings ? {} : settings });
        } catch (e) {
            console.debug(e);
        }
    }

    const getMenuItemsAsync = async () => {
        try {
            const menuItems = await apiClient.getMenuItemsAsync();
            const areLegacyItems = menuItems && menuItems.length > 0 && menuItems[0]["props"] === undefined;
            setState({ menuItems: areLegacyItems ? [] : menuItems });
        } catch (e) {
            console.debug(e);
        }
    }

    const getConfigurationAsync = async () => {
        try {
            const config = await Promise.all([
                apiClient.getSettingsAsync(),
                apiClient.getMenuItemsAsync()
            ]);
            const settings = config[0];
            const areLegacySettings = settings["AppID"] !== undefined;
            const menuItems = config[1];
            const areLegacyItems = menuItems && menuItems.length > 0 && menuItems[0]["props"] === undefined;
            setState({
                settings: areLegacySettings ? {} : settings,
                menuItems: areLegacyItems ? [] : menuItems,
                isLegacy: areLegacySettings || areLegacyItems
            });
            return true;
        } catch (e) {
            console.debug(e);
            return false;
        }
    }

    const checkConfigChangesAsync = async () => {
        try {
            const configStatus = await apiClient.checkConfigChangesAsync();
            setState({ configStatus });
        } catch (e) {
            console.debug(e);
        }
    }

    const saveConfigurationAsync = async (settings, menuItems) => {
        try {
            await Promise.all([
                apiClient.updateSettingsAsync({ settings }),
                apiClient.updateMenuItemsAsync({ menuItems })
            ]);
            setState({ configHasChanges: false });
            return true;
        } catch (e) {
            console.debug(e);
            return false;
        }
    }

    const resetConfigurationAsync = async () => {
        try {
            await Promise.all([
                apiClient.resetSettingsAsync(),
            ]);
            setState({
                configHasChanges: false,
                settings: {},
                menuItems: []
            });
            return true;
        } catch (e) {
            console.debug(e);
            return false;
        }
    }

    const updateLiveConfigAsync = async () => {
        try {
            const storedFile = await apiClient.updateLiveConfigAsync();
            return true;
        } catch (e) {
            console.debug(e);
            return false;
        }
    }

    const updateAppConfigAsync = async () => {
        try {
            await apiClient.updateAppConfigAsync();
            return true;
        } catch (e) {
            console.debug(e);
            return false;
        }
    }

    const getLiveViewerQrCodeAsync = async () => {
        try {
            const qrLiveViewer = await apiClient.getLiveViewerQrCodeAsync();
            setState({ qrLiveViewer });
        } catch (e) {
            console.debug(e);
            setState({ qrLiveViewer: null });
        }
    }

    const createAppleCertsAsync = async ({ keyId, teamId, email, bundleId }) => {
        try {
            const certsInfo = await apiClient.createAppleCertsAsync({ keyId, teamId, email, bundleId, adminEmail: plugin.adminEmail });
            return certsInfo;
        } catch (e) {
            console.debug(e);
            return null;
        }
    }

    const createGoogleCertsAsync = async ({ keystoreAlias, email, bundleId }) => {
        try {
            const certsInfo = await apiClient.createGoogleCertsAsync({ keystoreAlias, email, bundleId, adminEmail: plugin.adminEmail });
            return certsInfo;
        } catch (e) {
            console.debug(e);
            return null;
        }
    }

    const getWordPressCategoriesAsync = async (page, perPage) => {
        try {
            const categories = await apiClient.getWordPressCategoriesAsync(page, perPage);
            setState({ categories });
        } catch (e) {
            console.debug(e);
        }
    }

    const getWordPressPagesAsync = async (page, perPage) => {
        try {
            const pages = await apiClient.getWordPressPagesAsync(page, perPage);
            setState({ pages });
        } catch (e) {
            console.debug(e);
        }
    }

    const getIconClassesAsync = async () => {
        try {
            const icons = await apiClient.getIconClassesAsync();
            setState({ icons: icons });
        } catch (e) {
            console.debug(e);
        }
    }

    const mergeSettings = (tabSettings) => {
        const prevSettings = state.settings;
        const settings = { ...cloneDeep(state.settings), ...tabSettings };
        const hasChanges = state.configHasChanges || (JSON.stringify({
            ...prevSettings,
            enableiOSBuild: null,
            enableAndroidBuild: null,
        }) !== JSON.stringify({
            ...settings,
            enableiOSBuild: null,
            enableAndroidBuild: null,
        }));
        setState({ settings, configHasChanges: hasChanges });
        return settings;
    }

    const mergeMenuItems = (tabMenuItems) => {
        const prevMenuItems = state.menuItems;
        const menuItems = [].concat(tabMenuItems);
        const hasChanges = state.configHasChanges || (JSON.stringify(prevMenuItems) != JSON.stringify(menuItems));
        setState({ menuItems, configHasChanges: hasChanges });
        return menuItems;
    }

    const validateSettingsAsync = async (settings) => {
        let errors = [];
        await SettingsValidateSchema.Info.validate(settings, { abortEarly: false }).catch((err) => {
            err.inner.forEach(x => errors.push({ errorId: x.path, message: x.message }));
            if (err.inner && err.inner.length > 0)
                errors.push({ errorId: "info", message: err.name });
        })
        await SettingsValidateSchema.Assets.validate(settings, { abortEarly: false }).catch((err) => {
            err.inner.forEach(x => errors.push({ errorId: x.path, message: x.message }));
            if (err.inner && err.inner.length > 0)
                errors.push({ errorId: "assets", message: err.name });
        })

       await SettingsValidateSchema.Ios.validate(settings, { abortEarly: false }).catch((err) => {
          err.inner.forEach(x => errors.push({ errorId: x.path, message: x.message }));
          if (err.inner && err.inner.length > 0)
             errors.push({ errorId: "ios", message: err.name });
       });

       if (state.settings.enableAndroidBuild) {
          await SettingsValidateSchema.Ios1.validate(settings, { abortEarly: false }).catch((err) => {
             err.inner.forEach(x => errors.push({ errorId: x.path, message: x.message }));
             if (err.inner && err.inner.length > 0)
                errors.push({ errorId: "ios", message: err.name });
          });
       }

        if (state.settings.enableiOSBuild) {
            await SettingsValidateSchema.Ios2.validate(settings, { abortEarly: false }).catch((err) => {
                err.inner.forEach(x => errors.push({ errorId: x.path, message: x.message }));
                if (err.inner && err.inner.length > 0)
                    errors.push({ errorId: "ios", message: err.name });
            });
        }



        await SettingsValidateSchema.Services.validate(settings, { abortEarly: false }).catch((err) => {
            err.inner.forEach(x => errors.push({ errorId: x.path, message: x.message }));
            if (err.inner && err.inner.length > 0)
                errors.push({ errorId: "services", message: err.name });
        })

        return errors
    }

    const validateMenuItemsAsync = async (settings, menuItems) => {
        let errors = [];

        if (settings.enableAppMenu
            && (menuItems.length === 0
                || !menuItems.some(x => x.type === MenuItemTypes.HomePage))) {
            errors.push({ errorId: "missingHomePage", message: t("MissingHomePageError").replace("$type", t("MenuItemHomePage")) });
        }

        if (menuItems) {
            for (let index = 0; index < menuItems.length; index++) {
                const item = menuItems[index];
                await MenuItemValidateSchema[item.type]
                    .validate(item, { abortEarly: false })
                    .catch((err) => {
                        err.inner.forEach(x => errors.push({ errorId: `[${item.id}].${x.path}`, message: x.message }));
                    });
            }
        }

        if (errors.length > 0) {
            errors.push({ errorId: "menu", message: "ValidationError" });
        }

        return errors;
    }

    const validateAllAsync = async (settings, menuItems) => {
        let errors = [
            ...(await validateSettingsAsync(settings ?? state.settings)),
            ...(await validateMenuItemsAsync(settings ?? state.settings, menuItems ?? state.menuItems))
        ];
        console.debug(errors);
        setState({ errors });
        return !(errors.length > 0);
    }

    return (
        <Provider value={{
            state,
            pageIsReady,
            pageHasError,
            setPageIsReady,
            isBusy,
            setIsBusy,
            checkLicenseAsync,
            getSettingsAsync,
            getMenuItemsAsync,
            getConfigurationAsync,
            checkConfigChangesAsync,
            saveConfigurationAsync,
            resetConfigurationAsync,
            updateLiveConfigAsync,
            updateAppConfigAsync,
            getLiveViewerQrCodeAsync,
            createAppleCertsAsync,
            createGoogleCertsAsync,
            getWordPressCategoriesAsync,
            getWordPressPagesAsync,
            getIconClassesAsync,
            mergeSettings,
            mergeMenuItems,
            validateAllAsync,
        }}>
            {props.children}
        </Provider>
    );
}